import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import PasswordInput from '../../components/passwordInput/PasswordInput';
import { api } from '../../utils/api/apiClient';

interface ChangePasswordFormInput {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormInput>();

  const onSubmit: SubmitHandler<ChangePasswordFormInput> = async data => {
    setApiErrorMessage(null);
    setIsSubmitting(true);

    const passwordPattern = /^(?=.*[A-Z])(?=.*[\W_]).{8,}$/;

    if (data.newPassword !== data.confirmPassword) {
      setApiErrorMessage("New passwords don't match");
      setIsSubmitting(false);
      return;
    }

    if (!passwordPattern.test(data.newPassword)) {
      setApiErrorMessage(
        'New password must be at least 8 characters, contain an uppercase letter and a special character'
      );
      setIsSubmitting(false);
      return;
    }

    try {
      const result: any = await api.post('/pmo/u/u/p', data);

      if (result.status === 200) {
        toast.success('Password changed successfully.');
      } else {
        toast.error('Failed to change password. Please try again.');
      }
    } catch (err: any) {
      setApiErrorMessage(
        err?.message ?? 'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className='primary-shadow rounded-lg border border-gray-200 mt-6'>
      <Toaster />
      <form className='' onSubmit={handleSubmit(onSubmit)}>
        <div className='flex justify-between items-center text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
          <h2>Change Password</h2>
        </div>
        <div className='m-4 sm:m-8 space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full'>
            <PasswordInput
              id='currentPassword'
              label='Current Password'
              error={errors.currentPassword?.message}
              register={register('currentPassword', {
                required: 'Current Password is required',
              })}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full'>
            <PasswordInput
              id='newPassword'
              label='New Password'
              error={errors.newPassword?.message}
              register={register('newPassword', {
                required: 'New password is required',
              })}
            />

            <PasswordInput
              id='confirmPassword'
              label='Confirm Password'
              error={errors.confirmPassword?.message}
              register={register('confirmPassword', {
                required: 'Please confirm your password',
              })}
            />
          </div>
          {apiErrorMessage && (
            <p className='text-red-600 text-sm font-medium mt-1'>
              {apiErrorMessage}
            </p>
          )}
          <button
            type='submit'
            className='bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed w-fit px-4 py-3 !mt-8 min-w-44'
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </form>
    </section>
  );
};

export default ChangePassword;
